import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  Flex,
  Heading,
  Text,
  Table,
  Thead,
  Tr,
  Tbody,
  Td,
  Box,
  Spacer,
  Button,
  Th,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useMemo } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';
import {
  CreditAgreement,
  DataLake,
  Util,
} from '@finley-technologies/finley-types';

import { Icon } from 'packages/common-frontend/components/icons';
import { InfoAlertWithDescription } from 'packages/common-frontend/components/InfoAlertWithDescription';
import { PreviewPostgresQueryDrawerHeader } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/entity/covenantPostgresQuery/lenderView/LenderPostgresQueryComponents';
import { queryInputTypeToIcon } from 'packages/settings/ui/AddQueryInputModal';

const QueryResultSection = ({
  columnName,
  queryDataForColumn,
}: {
  columnName: string;
  queryDataForColumn: Util.Primitive[];
}) => {
  return (
    <Box mt="24px">
      <Text fontWeight={500} mb="16px">
        {columnName}
      </Text>
      <Table mt="24px" borderWidth="1px">
        <Thead>
          <Tr>
            <Th>Index</Th>
            <Th>Value</Th>
          </Tr>
        </Thead>
        <Tbody>
          {_.map(queryDataForColumn, (value, idx) => (
            <Tr key={`query-result-section-${columnName}-${idx}`}>
              <Td>{`${columnName}_idx${idx}`}</Td>
              <Td>{value}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};

type LenderAthenaQueryPreviewDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  queryDataItems: DataLake.AthenaQueryItemData[];
  athenaQuery: CreditAgreement.Config.AthenaConfig.Query;
  queryType: 'singleQuery';
  inputDate: Util.FullDateStr;
  inputDateType: 'reportingDate' | 'triggerDate';
};
type LenderAthenaRecurringQueryPreviewDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  queryDataItems: DataLake.AthenaQueryItemData[];
  athenaQuery: CreditAgreement.Config.AthenaConfig.RecurringQuery;
  queryType: 'multiQuery';
  inputDate: Util.FullDateStr;
  inputDateType: 'reportingDate' | 'triggerDate';
};
export const LenderAthenaQueryPreviewDrawer = ({
  isOpen,
  onClose,
  queryDataItems,
  athenaQuery,
  queryType,
  inputDate,
  inputDateType,
}:
  | LenderAthenaQueryPreviewDrawerProps
  | LenderAthenaRecurringQueryPreviewDrawerProps) => {
  const isQueryDataItemsEmpty = useMemo(
    () =>
      !queryDataItems.length ||
      _.every(queryDataItems, (item) => _.isEmpty(item)),
    [queryDataItems],
  );

  const queryDataItemsGrouped = useMemo(() => {
    if (isQueryDataItemsEmpty) {
      return null;
    }

    return _.reduce(
      queryDataItems,
      (acc, queryResultItem) => {
        _.forEach(queryResultItem, (queryValue, queryKey) => {
          if (!acc[queryKey]) {
            // eslint-disable-next-line no-param-reassign
            acc[queryKey] = [queryValue];
          } else {
            acc[queryKey].push(queryValue);
          }
        });
        return acc;
      },
      {} as { [key: string]: Util.Primitive[] },
    );
  }, [queryDataItems, isQueryDataItemsEmpty]);

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xl">
      <DrawerOverlay />
      <DrawerContent padding="16px">
        <DrawerHeader>
          <Flex alignItems="center">
            <Icon icon={queryInputTypeToIcon[queryType].icon} />
            <Heading
              as="h2"
              fontSize="24px"
              lineHeight="32px"
              fontWeight={600}
              ml="16px"
            >
              {queryType === 'singleQuery'
                ? athenaQuery.query.name
                : athenaQuery.name}
            </Heading>
            <Spacer />
            <Button variant="ghost" onClick={onClose}>
              <Icon icon="x" />
            </Button>
          </Flex>
        </DrawerHeader>

        <DrawerBody>
          <PreviewPostgresQueryDrawerHeader
            header={`Preview ${commonUtils.toTitleCase(inputDateType)}`}
            marginStyles={{ marginTop: '8px', marginBottom: '8px' }}
          />
          <Text mt="24px">{inputDate}</Text>

          {isQueryDataItemsEmpty ? (
            <>
              <PreviewPostgresQueryDrawerHeader
                header="Query"
                marginStyles={{ marginTop: '32px', marginBottom: '8px' }}
              />
              <Box mt="24px">
                <InfoAlertWithDescription description="This query returned no data for the selected date." />
              </Box>
            </>
          ) : (
            <>
              <PreviewPostgresQueryDrawerHeader
                header="Query"
                marginStyles={{ marginTop: '32px' }}
              />
              {_.map(
                queryDataItemsGrouped,
                (queryDataForColumn, columnName) => (
                  <QueryResultSection
                    columnName={columnName}
                    queryDataForColumn={queryDataForColumn}
                    key={columnName}
                  />
                ),
              )}

              <PreviewPostgresQueryDrawerHeader
                header="Column Headers"
                marginStyles={{ marginTop: '32px' }}
              />
              <Table mt="24px" borderWidth="1px">
                <Thead>
                  <Tr>
                    <Th>Column Header</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {_.map(Object.keys(queryDataItems[0]), (columnHeader) => (
                    <Tr key={`query-column-header-${columnHeader}`}>
                      <Td>{columnHeader}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
